import { useState, useEffect, useRef } from 'react'
import { apiClient } from '../utils/apiClient'
import { City } from '../Components/Common/CitySelector'
import { ApiError } from '../utils/apiClient'

// Shared cache for cities data
let citiesCache: City[] | null = null
let citiesPromise: Promise<City[]> | null = null
let isLoadingCache = false

interface CitiesError {
    message: string
    status?: number
}

/**
 * Shared hook for fetching cities data with caching
 * Prevents duplicate API calls across multiple components
 */
export const useCities = () => {
    const [cities, setCities] = useState<City[]>(citiesCache || [])
    const [isLoading, setIsLoading] = useState(isLoadingCache)
    const [error, setError] = useState<CitiesError | null>(null)
    const hasFetchedRef = useRef(false)

    useEffect(() => {
        // If we already have cached data, use it
        if (citiesCache && citiesCache.length > 0) {
            setCities(citiesCache)
            setIsLoading(false)
            setError(null)
            return
        }

        // If a fetch is already in progress, wait for it
        if (citiesPromise && !hasFetchedRef.current) {
            hasFetchedRef.current = true
            citiesPromise
                .then((data) => {
                    setCities(data)
                    setIsLoading(false)
                    setError(null)
                })
                .catch((err) => {
                    setIsLoading(false)
                    hasFetchedRef.current = false
                    const apiError = err as ApiError
                    setError({
                        message:
                            apiError?.message ||
                            'An error occurred while fetching cities',
                        status: apiError?.status,
                    })
                })
            return
        }

        // Start a new fetch if needed
        if (!citiesPromise && !hasFetchedRef.current) {
            hasFetchedRef.current = true
            setIsLoading(true)
            isLoadingCache = true
            setError(null)

            citiesPromise = apiClient
                .get<City[]>('website/cities')
                .then((response) => {
                    citiesCache = response.data
                    isLoadingCache = false
                    setCities(response.data)
                    setIsLoading(false)
                    setError(null)
                    return response.data
                })
                .catch((err) => {
                    console.error('Error fetching cities:', err)
                    isLoadingCache = false
                    setIsLoading(false)
                    hasFetchedRef.current = false
                    citiesPromise = null
                    const apiError = err as ApiError
                    setError({
                        message:
                            apiError?.message ||
                            'An error occurred while fetching cities',
                        status: apiError?.status,
                    })
                    // Return empty array to maintain promise type
                    return [] as City[]
                })
        }
    }, [])

    return { cities, isLoading, error }
}

/**
 * Clear the cities cache (useful for testing or forced refresh)
 */
export const clearCitiesCache = () => {
    citiesCache = null
    citiesPromise = null
    isLoadingCache = false
}
